// EnhancedScenarioManager.tsx - Integration layer with UX enhancements

import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useToast } from '../../hooks/useToast';
import { useKeyboardShortcuts, KeyboardShortcutsHelp, KeyboardShortcut } from '../../hooks/useKeyboardShortcuts';
import { Scenario } from '../../types/ScenarioManagement';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { HelpIcon, DocumentationPanel } from '../ui/HelpTooltip';
import ScenarioList from './ScenarioList';
import SimpleScenarioForm from './SimpleScenarioForm';
import ScenarioEdit from './ScenarioEdit';
// Removed unused useTheme import

interface EnhancedScenarioManagerProps {
  mode: 'list' | 'create' | 'edit';
  scenarioId?: string;
  onModeChange: (mode: 'list' | 'create' | 'edit', scenarioId?: string) => void;
}

export const EnhancedScenarioManager: React.FC<EnhancedScenarioManagerProps> = ({
  mode,
  scenarioId,
  onModeChange,
}) => {
  const { showToast } = useToast();
  // Removed unused isDark from useTheme
  const [loadingStates, setLoadingStates] = useState({
    deleting: false,
    saving: false,
    loading: false,
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    type: 'delete' | 'bulk-delete' | 'discard-changes';
    title: string;
    message: string;
    onConfirm: () => void;
    scenarioIds?: string[];
  }>({
    isOpen: false,
    type: 'delete',
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [helpPanel, setHelpPanel] = useState({
    isOpen: false,
    context: 'general' as 'general' | 'create' | 'edit',
  });

  // Define keyboard shortcuts
  const shortcuts: KeyboardShortcut[] = [
    {
      key: 'n',
      ctrl: true,
      description: 'Create new scenario',
      handler: () => {
        if (mode === 'list') {
          onModeChange('create');
          showToast({ type: 'info', title: 'New Scenario', message: 'Creating new scenario...', duration: 2000 });
        }
      },
      enabled: mode === 'list',
    },
    {
      key: 's',
      ctrl: true,
      description: 'Save current scenario',
      handler: () => {
        if (mode === 'create' || mode === 'edit') {
          showToast({ type: 'info', title: 'Save Shortcut', message: 'Use the save button or form submission', duration: 3000 });
        }
      },
      enabled: mode === 'create' || mode === 'edit',
    },
    {
      key: 'f',
      ctrl: true,
      description: 'Focus search field',
      handler: () => {
        if (mode === 'list') {
          const searchInput = document.querySelector('input[placeholder*="search"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            showToast({ type: 'info', title: 'Search Active', message: 'Search field focused', duration: 2000 });
          }
        }
      },
      enabled: mode === 'list',
    },
    {
      key: 'Escape',
      description: 'Cancel current action',
      handler: () => {
        if (mode === 'create' || mode === 'edit') {
          onModeChange('list');
        }
      },
      enabled: mode === 'create' || mode === 'edit',
    },
    {
      key: 'h',
      ctrl: true,
      description: 'Show help panel',
      handler: () => {
        setHelpPanel({ isOpen: true, context: mode === 'list' ? 'general' : mode });
      },
    },
  ];

  // Initialize keyboard shortcuts
  const { isHelpOpen, setIsHelpOpen } = useKeyboardShortcuts({
    shortcuts,
    enabled: true,
  });

  // Enhanced delete handler with confirmation and loading states
  const handleDelete = useCallback(async (scenarioIds: string[]) => {
    const isMultiple = scenarioIds.length > 1;
    
    setConfirmDialog({
      isOpen: true,
      type: isMultiple ? 'bulk-delete' : 'delete',
      title: isMultiple ? 'Delete Multiple Scenarios' : 'Delete Scenario',
      message: isMultiple 
        ? `Are you sure you want to delete ${scenarioIds.length} scenarios? This action cannot be undone.`
        : 'Are you sure you want to delete this scenario? This action cannot be undone.',
      scenarioIds,
      onConfirm: async () => {
        setLoadingStates(prev => ({ ...prev, deleting: true }));
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        
        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          showToast({
            type: 'success',
            title: isMultiple ? 'Scenarios Deleted' : 'Scenario Deleted',
            message: isMultiple 
              ? `Successfully deleted ${scenarioIds.length} scenarios`
              : 'The scenario has been permanently deleted',
            duration: 5000,
          });
          
          // Refresh the list or navigate away
          if (mode === 'edit') {
            onModeChange('list');
          }
          
        } catch (error) {
          showToast({
            type: 'error',
            title: 'Delete Failed',
            message: 'Failed to delete the scenario(s). Please try again.',
            duration: 7000,
            action: {
              label: 'Retry',
              onClick: () => handleDelete(scenarioIds),
            },
          });
        } finally {
          setLoadingStates(prev => ({ ...prev, deleting: false }));
        }
      },
    });
  }, [mode, onModeChange, showToast]);

  // Enhanced save handler with optimistic updates and error handling
  const handleSave = useCallback(async (formData: any) => {
    setLoadingStates(prev => ({ ...prev, saving: true }));
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      showToast({
        type: 'success',
        title: mode === 'create' ? 'Scenario Created' : 'Scenario Updated',
        message: mode === 'create' 
          ? 'Your new scenario has been created successfully'
          : 'All changes have been saved',
        duration: 5000,
        action: {
          label: 'View List',
          onClick: () => onModeChange('list'),
        },
      });
      
      // Navigate back to list
      onModeChange('list');
      
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Save Failed',
        message: 'Failed to save the scenario. Please check your connection and try again.',
        duration: 7000,
        action: {
          label: 'Retry',
          onClick: () => handleSave(formData),
        },
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, saving: false }));
    }
  }, [mode, onModeChange, showToast]);

  // Enhanced restore handler for soft-deleted scenarios
  const handleRestore = useCallback(async (scenarioIds: string[]) => {
    setLoadingStates(prev => ({ ...prev, loading: true }));
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      showToast({
        type: 'success',
        title: 'Scenarios Restored',
        message: `Successfully restored ${scenarioIds.length} scenario(s)`,
        duration: 4000,
      });
      
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Restore Failed',
        message: 'Failed to restore scenarios. Please try again.',
        duration: 5000,
        action: {
          label: 'Retry',
          onClick: () => handleRestore(scenarioIds),
        },
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, loading: false }));
    }
  }, [showToast]);

  // Enhanced export handler with format options
  const handleExport = useCallback(async (scenarioIds: string[]) => {
    setLoadingStates(prev => ({ ...prev, loading: true }));
    
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      showToast({
        type: 'success',
        title: 'Export Complete',
        message: `Successfully exported ${scenarioIds.length} scenario(s)`,
        duration: 4000,
        action: {
          label: 'Download',
          onClick: () => {
            // Simulate download
            console.log('Downloading exported scenarios...');
          },
        },
      });
      
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export scenarios. Please try again.',
        duration: 5000,
        action: {
          label: 'Retry',
          onClick: () => handleExport(scenarioIds),
        },
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, loading: false }));
    }
  }, [showToast]);

  // Help content for different contexts
  const getHelpContent = () => {
    switch (helpPanel.context) {
      case 'create':
        return [
          {
            title: 'Creating Scenarios',
            content: (
              <div className="space-y-4">
                <p>Follow these steps to create effective scenarios:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Start with a clear, compelling title and subtitle</li>
                  <li>Choose the appropriate difficulty level and estimated duration</li>
                  <li>Define specific, measurable objectives</li>
                  <li>Set up realistic resources and constraints</li>
                  <li>Create a timeline that builds tension progressively</li>
                </ul>
              </div>
            ),
          },
          {
            title: 'Best Practices',
            content: (
              <div className="space-y-4">
                <ul className="space-y-2 list-disc list-inside">
                  <li>Base scenarios on real-world incidents when possible</li>
                  <li>Ensure decisions have meaningful consequences</li>
                  <li>Test scenarios before publishing</li>
                  <li>Include multiple valid solution paths</li>
                  <li>Consider accessibility and inclusive design</li>
                </ul>
              </div>
            ),
          },
        ];
      case 'edit':
        return [
          {
            title: 'Editing Scenarios',
            content: (
              <div className="space-y-4">
                <p>When editing existing scenarios:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Review player feedback and analytics</li>
                  <li>Test changes thoroughly before publishing</li>
                  <li>Consider version control for major changes</li>
                  <li>Update related scenarios if dependencies change</li>
                </ul>
              </div>
            ),
          },
        ];
      default:
        return [
          {
            title: 'Scenario Management',
            content: (
              <div className="space-y-4">
                <p>Manage your cybersecurity scenarios effectively:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Use filters and search to find scenarios quickly</li>
                  <li>Organize scenarios with tags and categories</li>
                  <li>Monitor performance through analytics</li>
                  <li>Export scenarios for backup or sharing</li>
                </ul>
              </div>
            ),
          },
          {
            title: 'Keyboard Shortcuts',
            content: (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="font-mono bg-gray-600 px-2 py-1 rounded">Ctrl + N</span>
                  <span>New Scenario</span>
                  <span className="font-mono bg-gray-600 px-2 py-1 rounded">Ctrl + S</span>
                  <span>Save</span>
                  <span className="font-mono bg-gray-600 px-2 py-1 rounded">Ctrl + F</span>
                  <span>Search</span>
                  <span className="font-mono bg-gray-600 px-2 py-1 rounded">Esc</span>
                  <span>Cancel/Close</span>
                </div>
              </div>
            ),
          },
        ];
    }
  };

  const renderContent = () => {
    switch (mode) {
      case 'list':
        return (
          <ScenarioList
            onEdit={(scenario: Scenario) => onModeChange('edit', scenario.id)}
            onDelete={handleDelete}
            onRestore={handleRestore}
            onExport={handleExport}
            onCreate={() => onModeChange('create')}
          />
        );
      
      case 'create':
        return (
          <SimpleScenarioForm
            mode="create"
            onSubmit={handleSave}
            onCancel={() => {
              setConfirmDialog({
                isOpen: true,
                type: 'discard-changes',
                title: 'Discard Changes?',
                message: 'Are you sure you want to discard your changes? All unsaved data will be lost.',
                onConfirm: () => {
                  setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                  onModeChange('list');
                },
              });
            }}
          />
        );
      
      case 'edit':
        return scenarioId ? (
          <ScenarioEdit
            scenarioId={scenarioId}
            onSave={handleSave}
            onCancel={() => onModeChange('list')}
            onDelete={() => handleDelete([scenarioId])}
          />
        ) : (
          <div className="text-center text-gray-400 py-8">
            <p>No scenario selected for editing</p>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="relative">
      {/* Header with help */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold text-white">
            {mode === 'create' && 'Create Scenario'}
            {mode === 'edit' && 'Edit Scenario'}
            {mode === 'list' && 'Scenario Management'}
          </h1>
          <HelpIcon
            content="Click for help and keyboard shortcuts"
            size="md"
          />
        </div>
        
        <button
          onClick={() => setHelpPanel({ isOpen: true, context: mode === 'list' ? 'general' : mode })}
          className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-all duration-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Help & Shortcuts
        </button>
      </motion.div>

      {/* Loading overlay */}
      {(loadingStates.deleting || loadingStates.saving || loadingStates.loading) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-black/20 backdrop-blur-sm z-40 flex items-center justify-center"
        >
          <div className="bg-navy-900 rounded-lg p-6 shadow-2xl border border-white/10">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-professional-blue-500"></div>
              <span className="text-white">
                {loadingStates.deleting && 'Deleting scenario...'}
                {loadingStates.saving && 'Saving changes...'}
                {loadingStates.loading && 'Processing...'}
              </span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Main content */}
      <motion.div
        key={mode}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        {renderContent()}
      </motion.div>

      {/* Confirmation Dialog */}
      <ConfirmationModal
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.type === 'delete' || confirmDialog.type === 'bulk-delete' ? 'Delete' : 'Discard'}
        type={confirmDialog.type === 'delete' || confirmDialog.type === 'bulk-delete' ? 'danger' : 'warning'}
        loading={loadingStates.deleting || loadingStates.saving}
      />

      {/* Help Documentation Panel */}
      <DocumentationPanel
        isOpen={helpPanel.isOpen}
        onClose={() => setHelpPanel(prev => ({ ...prev, isOpen: false }))}
        title="Scenario Management Help"
        sections={getHelpContent()}
      />

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        shortcuts={shortcuts}
      />
    </div>
  );
};

export default EnhancedScenarioManager;